import { GuideMenu } from '@/components/GuideMenu';
import { createFileRoute, Link } from '@tanstack/react-router';

const OverviewSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Overview: Connecting Mythea to Discord
    </h3>
    <p className="text-[#b8b8b8] mb-2 leading-relaxed">
      Mythea offers a comprehensive integration with Discord, linking the game world
      to both the official community server and private kingdom servers. Through the
      official Mythea Bot, real-time event webhooks, and live data bridges, rulers can
      monitor the realm, coordinate with their kingdom, calculate attack outcomes, and
      receive immediate notifications on mobile or desktop when their province is attacked.
    </p>
    <p className="text-[#b8b8b8] mb-2 leading-relaxed">
      Whether you are actively tracking wars and arcane auctions, a monarch orchestrating
      kingdom-wide defenses, or a general coordinating a wave strike, Discord integration
      brings vital tactical tools directly into your chat.
    </p>
  </div>
);

const AccountLinkingSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Connecting Your Account & Citizen Verification
    </h3>
    <p className="text-[#b8b8b8] mb-2 leading-relaxed">
      Connecting your Discord account unlocks citizen status in the official Discord
      server, synchronizes your in-game identity, and activates personalized bot commands
      and direct message alerts.
    </p>
    <div className="glass-subtle rounded-xl p-4 border border-[#d4af37]/20 space-y-3">
      <h4 className="text-base font-semibold text-[#f0d060]">How to Connect:</h4>
      <ol className="text-[#b8b8b8] list-decimal list-inside space-y-1.5 leading-relaxed">
        <li>Log into the main realm and open the <strong>Preferences</strong> menu.</li>
        <li>Select <strong>Account Settings</strong>.</li>
        <li>Click the <strong>Connect Discord</strong> button to open Discord&apos;s secure authorization page.</li>
        <li>Authorize the connection. Your accounts are instantly linked!</li>
      </ol>
      <p className="text-sm text-[#888] pt-1">
        You can also type <code className="text-yellow-300 font-mono">/link</code> in Discord at any time to check your status or receive a private one-click connection prompt.
      </p>
    </div>
    <div className="space-y-2 pt-2">
      <h4 className="text-base font-semibold text-[#f0d060]">Key Benefits:</h4>
      <ul className="text-[#b8b8b8] list-disc list-inside space-y-1.5 leading-relaxed">
        <li><strong>Citizen Verification:</strong> Automatically confirms citizen standing in the official Discord server.</li>
        <li><strong>Identity & Role Sync:</strong> Synchronizes your Discord server nickname with your game chat name, and assigns roles reflecting your Hall of Greatness rank and Monarch crown.</li>
        <li><strong>Personalized Slash Commands:</strong> Enables <code className="text-yellow-300 font-mono">/prov</code> (without arguments) to pull up your province, <code className="text-yellow-300 font-mono">/plan</code> for your kingdom&apos;s plans, and <code className="text-yellow-300 font-mono">/aid</code> for supplies.</li>
        <li><strong>Direct Message Alerts:</strong> Delivers instant DM alerts for incoming attacks, private messages, and watched auctions.</li>
        <li><strong>One-to-One Security:</strong> Each Discord account links to exactly one Mythea account, and you may disconnect at any time from Account Settings.</li>
      </ul>
    </div>
  </div>
);

const BotInviteSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Adding the Bot to Your Private Kingdom Server
    </h3>
    <p className="text-[#b8b8b8] mb-2 leading-relaxed">
      You can bring the Mythea Bot into your kingdom&apos;s private Discord server, allowing
      your team to run calculations, inspect intel, check rankings, and request aid directly
      within your private war channels.
    </p>
    <div className="glass-subtle rounded-xl p-4 border border-[#d4af37]/20 space-y-3">
      <h4 className="text-base font-semibold text-[#f0d060]">Inviting the Bot:</h4>
      <p className="text-[#b8b8b8] leading-relaxed">
        Navigate to <strong>Account Settings</strong> and click <strong>Add to Discord</strong>.
        You must hold the <em>Manage Server</em> permission on the target Discord server to install bots.
      </p>
      <p className="text-[#b8b8b8] leading-relaxed">
        The bot requires only minimal standard permissions: <strong>View Channels</strong>, <strong>Send Messages</strong>,
        and <strong>Embed Links</strong>. It requests no administrative rights and no privileged message-reading intents.
      </p>
    </div>
  </div>
);

const LiveChannelsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Official Discord Live Channels
    </h3>
    <p className="text-[#b8b8b8] mb-2 leading-relaxed">
      The official Mythea Discord server provides several real-time channels fed directly from the game engines:
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="glass-subtle rounded-xl p-4 border border-[#d4af37]/20 space-y-2">
        <h4 className="text-lg font-semibold text-[#f0d060] flex items-center gap-2">
          <span>💬</span> #world-chat
        </h4>
        <p className="text-sm text-[#b8b8b8] leading-relaxed">
          Bi-directional mirror of in-game World Chat (<code className="text-yellow-300 font-mono">4p</code> on production, <code className="text-yellow-300 font-mono">3p</code> on Arena).
          Supports text formatting (bold, italic, colors) and mirrors herald events like dragon flights and war starts. Province coordinates are never disclosed to maintain player privacy.
        </p>
      </div>
      <div className="glass-subtle rounded-xl p-4 border border-[#d4af37]/20 space-y-2">
        <h4 className="text-lg font-semibold text-[#f0d060] flex items-center gap-2">
          <span>⚔️</span> #war-declarations
        </h4>
        <p className="text-sm text-[#b8b8b8] leading-relaxed">
          Live broadcast of realm-wide war declarations. Whenever conflict breaks out between kingdoms,
          a herald notice details the aggressive kingdom, target kingdom, and declaration text.
        </p>
      </div>
      <div className="glass-subtle rounded-xl p-4 border border-[#d4af37]/20 space-y-2">
        <h4 className="text-lg font-semibold text-[#f0d060] flex items-center gap-2">
          <span>🏛️</span> #auction-house
        </h4>
        <p className="text-sm text-[#b8b8b8] leading-relaxed">
          Continuously updated live embed tracking the Arcane Auction lot, current high bid, top bidder,
          item stats, countdown timer, and upcoming lots in the queue.
        </p>
      </div>
      <div className="glass-subtle rounded-xl p-4 border border-[#d4af37]/20 space-y-2">
        <h4 className="text-lg font-semibold text-[#f0d060] flex items-center gap-2">
          <span>📡</span> Activity Wire
        </h4>
        <p className="text-sm text-[#b8b8b8] leading-relaxed">
          Public aggregate telemetry board (<code className="text-yellow-300 font-mono">MYTHEA WIRE</code> &amp; <code className="text-yellow-300 font-mono">ARENA WIRE</code>).
          Updates every minute with 15-minute rolling counts of players active, attacks, spells, thief ops, dragons, explores, builds, training, and aid.
          Includes an hourly tick history thread preserving total player anonymity.
        </p>
      </div>
    </div>
  </div>
);

const KingdomWebhooksSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Kingdom Webhooks for Monarchs
    </h3>
    <p className="text-[#b8b8b8] mb-2 leading-relaxed">
      Monarchs and Co-Monarchs can link a private Discord channel webhook to receive automated kingdom herald notices in real time.
    </p>
    <div className="glass-subtle rounded-xl p-4 border border-[#d4af37]/20 space-y-3">
      <h4 className="text-base font-semibold text-[#f0d060]">Setup &amp; Testing:</h4>
      <ol className="text-[#b8b8b8] list-decimal list-inside space-y-1.5 leading-relaxed">
        <li>Create a Webhook in your private kingdom Discord channel (<em>Channel Settings &rarr; Integrations &rarr; Webhooks</em>).</li>
        <li>In-game, go to <strong>Politics &rarr; Monarchy</strong>.</li>
        <li>Paste your webhook URL into the <strong>Kingdom Discord Webhook</strong> field and save.</li>
        <li>Click the <strong>Send Test Notice</strong> button to verify that the webhook is receiving messages.</li>
      </ol>
      <h4 className="text-base font-semibold text-[#f0d060] pt-2">Automated Kingdom Broadcasts:</h4>
      <ul className="text-[#b8b8b8] list-disc list-inside space-y-1 leading-relaxed">
        <li><strong>Incoming Attacks:</strong> Instant alert when hostile forces march toward any province in your kingdom, complete with attacker origin and war horn notice.</li>
        <li><strong>Outgoing Attacks:</strong> Records when your kingdom members launch strikes.</li>
        <li><strong>Aid Shipments:</strong> Logs aid sent and received between kingdom provinces.</li>
        <li><strong>War Events:</strong> War starts, peace declarations, and stance transitions.</li>
      </ul>
    </div>
  </div>
);

const DirectMessageAlertsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Direct Message (DM) Notifications
    </h3>
    <p className="text-[#b8b8b8] mb-2 leading-relaxed">
      Linked rulers can receive urgent direct messages from the Mythea Bot when key events occur while away from the game:
    </p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="bg-white/[0.03] p-3 rounded-lg border border-white/10 space-y-1">
        <h5 className="font-semibold text-yellow-300">🛡️ Incoming Attacks</h5>
        <p className="text-sm text-[#b8b8b8]">Immediate DM warning when an enemy army is launched at your province.</p>
      </div>
      <div className="bg-white/[0.03] p-3 rounded-lg border border-white/10 space-y-1">
        <h5 className="font-semibold text-yellow-300">✉️ Private Messages</h5>
        <p className="text-sm text-[#b8b8b8]">Alert when another province ruler sends you an in-game message.</p>
      </div>
      <div className="bg-white/[0.03] p-3 rounded-lg border border-white/10 space-y-1">
        <h5 className="font-semibold text-yellow-300">💎 Watched Auctions</h5>
        <p className="text-sm text-[#b8b8b8]">Notifies you the moment an item from your watchlist enters the active auction.</p>
      </div>
    </div>
    <p className="text-sm text-[#b8b8b8] leading-relaxed">
      Configure your alert preferences at any time using the <code className="text-yellow-300 font-mono">/alerts</code> slash command
      (options: <code className="text-yellow-300 font-mono">view</code>, <code className="text-yellow-300 font-mono">toggle_pm</code>, <code className="text-yellow-300 font-mono">toggle_attack</code>, <code className="text-yellow-300 font-mono">toggle_auction</code>, or <code className="text-yellow-300 font-mono">toggle_all</code>)
      or via Account Settings in-game. Includes quiet hours and rate-limiting to prevent spam.
    </p>
  </div>
);

const WatchlistAndAidSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Auction Watchlist, Aid Coordination &amp; Intel
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="glass-subtle rounded-xl p-4 border border-[#d4af37]/20 space-y-2">
        <h4 className="text-base font-semibold text-[#f0d060]">Auction Watchlist</h4>
        <p className="text-xs text-[#888] font-mono">/watch [item] [action]</p>
        <p className="text-sm text-[#b8b8b8] leading-relaxed">
          Add items to your personal watchlist (<code className="text-yellow-300 font-mono">action: add</code>), view active watches (<code className="text-yellow-300 font-mono">action: list</code>), or remove items.
          The bot sends you a DM alert the minute the lot is listed!
        </p>
      </div>
      <div className="glass-subtle rounded-xl p-4 border border-[#d4af37]/20 space-y-2">
        <h4 className="text-base font-semibold text-[#f0d060]">Kingdom Aid</h4>
        <p className="text-xs text-[#888] font-mono">/aid [action] [resource] [amount]</p>
        <p className="text-sm text-[#b8b8b8] leading-relaxed">
          List pending kingdom requests (<code className="text-yellow-300 font-mono">action: list</code>) or post an emergency request for Gold, Food, Runes, Soldiers, Land, Peasants, or Horses directly from Discord.
        </p>
      </div>
      <div className="glass-subtle rounded-xl p-4 border border-[#d4af37]/20 space-y-2">
        <h4 className="text-base font-semibold text-[#f0d060]">Plans &amp; Intel</h4>
        <p className="text-xs text-[#888] font-mono">/plan, /intel, /m</p>
        <p className="text-sm text-[#b8b8b8] leading-relaxed">
          View your kingdom&apos;s War Room campaign plans (<code className="text-yellow-300 font-mono">/plan</code>), inspect gathered spy reports (<code className="text-yellow-300 font-mono">/intel</code>), or run quick dot commands (<code className="text-yellow-300 font-mono">/m line: .i sm 7:5 s3</code>).
        </p>
      </div>
    </div>
  </div>
);

const SlashCommandsTable = () => {
  const commands = [
    {
      command: '/age',
      args: 'None',
      description: 'Show current Mythea age, active tick, tick length, lock status, and countdown to next tick.',
    },
    {
      command: '/kd',
      args: 'location: <X:Y | id | name>',
      description: "Look up a kingdom's monarch, province count, land, networth, stance, and active war details.",
    },
    {
      command: '/prov',
      args: 'name: [province name or id]',
      description: 'Look up province stats, land, networth, and honor (defaults to your own province if linked).',
    },
    {
      command: '/wars',
      args: 'None',
      description: 'View all active realm wars, conflict duration in ticks, and war points.',
    },
    {
      command: '/calc',
      args: 'offense: <OME> defense: <DME> [target_acres] [your_acres]',
      description: 'Calculate military attack odds, break margin percentage, and estimated land gains.',
    },
    {
      command: '/rankings',
      args: 'category: [choice]',
      description: 'View top kingdoms or provinces ranked by Land, Networth, or Honor.',
    },
    {
      command: '/link',
      args: 'None',
      description: 'Check your linked account status or get the secure one-click link prompt.',
    },
    {
      command: '/status',
      args: 'target: [all | production | staging]',
      description: 'Check real-time health, server latency percentiles, and container uptime.',
    },
    {
      command: '/alerts',
      args: 'action: [view | toggle_pm | toggle_attack | toggle_auction | toggle_all]',
      description: 'View or toggle your Discord direct message game notification preferences.',
    },
    {
      command: '/watch',
      args: 'item: [item name or id] action: [list | add | remove]',
      description: 'Manage your personal auction watchlist to receive DM alerts for upcoming lots.',
    },
    {
      command: '/aid',
      args: 'action: [list | add] [resource] [amount]',
      description: 'View pending kingdom aid requests, or post a new request directly to the aid board.',
    },
    {
      command: '/plan',
      args: 'kingdom: [X:Y location or id]',
      description: "View your kingdom's War Room campaign plans and target assignments.",
    },
    {
      command: '/intel',
      args: 'kingdom: <X:Y> op: [cb|sm|sv|ss|in|ae] province: [id|s3|name]',
      description: 'View the latest intelligence reports held by your kingdom on an enemy province.',
    },
    {
      command: '/m',
      args: 'line: <command line>',
      description: 'Execute quick dot-command intel queries (e.g. .i sm 7:5 s3 or .i intel 7:5).',
    },
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-[#e6c547]">
        Slash Commands Reference
      </h3>
      <div className="overflow-x-auto rounded-lg border border-white/10">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-[#d4af37]/20 bg-[#d4af37]/10 text-[#f0d060]">
              <th className="p-3 font-semibold">Command</th>
              <th className="p-3 font-semibold">Arguments</th>
              <th className="p-3 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {commands.map((row, index) => (
              <tr
                key={row.command}
                className={`transition-colors hover:bg-gray-800/40 ${
                  index % 2 === 0 ? 'bg-white/[0.02]' : 'bg-transparent'
                }`}
              >
                <td className="p-3 font-mono font-bold text-yellow-300 whitespace-nowrap">
                  {row.command}
                </td>
                <td className="p-3 font-mono text-xs text-[#a0a0a0]">
                  {row.args}
                </td>
                <td className="p-3 text-sm text-[#b8b8b8]">
                  {row.description}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/discord/')({
  component: DiscordPage,
});

function DiscordPage() {
  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        <GuideMenu currentPath="/discord" />

        {/* Main content section */}
        <section className="glass rounded-2xl p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <img
              src="/images/nav/chat@2x.png"
              alt="Discord Integration"
              width={80}
              height={80}
              className="shrink-0 rounded-lg border border-[#d4af37]/35 object-cover"
            />
            <div>
              <h2 className="text-2xl font-bold font-cinzel text-[#e6c547]">Discord Integration</h2>
              <p className="text-sm text-[#a0a0a0]">Official bot, slash commands, alerts, kingdom webhooks, and live feeds.</p>
            </div>
          </div>

          {/* Content sections */}
          <div className="space-y-12 text-[#b8b8b8] leading-relaxed">
            <OverviewSection />
            <AccountLinkingSection />
            <BotInviteSection />
            <LiveChannelsSection />
            <KingdomWebhooksSection />
            <DirectMessageAlertsSection />
            <WatchlistAndAidSection />
            <SlashCommandsTable />
          </div>

          {/* Footer cross links */}
          <div className="mt-12 pt-6 border-t border-white/10 flex flex-wrap gap-4 text-sm">
            <Link
              to="/classic-guide/discord"
              className="text-[#e6c547] hover:underline flex items-center gap-1"
            >
              <span>&larr;</span> View Classic Guide view of Discord Integration
            </Link>
            <span className="text-[#555]">&bull;</span>
            <Link
              to="/communication"
              className="text-[#e6c547] hover:underline"
            >
              In-Game Communication Guide
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
